"use client";

import {
  BarChart3,
  Languages,
  Palette,
  TrendingUp,
  Upload,
  Video,
} from "lucide-react";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";

const features = [
  {
    icon: Video,
    titleKey: "features.deepVideoAnalysis.title",
    descriptionKey: "features.deepVideoAnalysis.description",
    stats: [
      {
        labelKey: "features.deepVideoAnalysis.stats.context.label",
        valueKey: "features.deepVideoAnalysis.stats.context.value",
      },
      {
        labelKey: "features.deepVideoAnalysis.stats.speech.label",
        valueKey: "features.deepVideoAnalysis.stats.speech.value",
      },
      {
        labelKey: "features.deepVideoAnalysis.stats.meaning.label",
        valueKey: "features.deepVideoAnalysis.stats.meaning.value",
      },
    ],
  },
  {
    icon: Palette,
    titleKey: "features.aiThumbnails.title",
    descriptionKey: "features.aiThumbnails.description",
    stats: [
      {
        labelKey: "features.aiThumbnails.stats.source.label",
        valueKey: "features.aiThumbnails.stats.source.value",
      },
      {
        labelKey: "features.aiThumbnails.stats.options.label",
        valueKey: "features.aiThumbnails.stats.options.value",
      },
      {
        labelKey: "features.aiThumbnails.stats.text.label",
        valueKey: "features.aiThumbnails.stats.text.value",
      },
    ],
  },
  {
    icon: TrendingUp,
    titleKey: "features.multiPlatformSeo.title",
    descriptionKey: "features.multiPlatformSeo.description",
    stats: [
      {
        labelKey: "features.multiPlatformSeo.stats.platforms.label",
        valueKey: "features.multiPlatformSeo.stats.platforms.value",
      },
      {
        labelKey: "features.multiPlatformSeo.stats.angles.label",
        valueKey: "features.multiPlatformSeo.stats.angles.value",
      },
      {
        labelKey: "features.multiPlatformSeo.stats.intent.label",
        valueKey: "features.multiPlatformSeo.stats.intent.value",
      },
    ],
  },
  {
    icon: Languages,
    titleKey: "features.languages.title",
    descriptionKey: "features.languages.description",
    stats: [
      {
        labelKey: "features.languages.stats.languages.label",
        valueKey: "features.languages.stats.languages.value",
      },
      {
        labelKey: "features.languages.stats.dubbing.label",
        valueKey: "features.languages.stats.dubbing.value",
      },
      {
        labelKey: "features.languages.stats.voiceClone.label",
        valueKey: "features.languages.stats.voiceClone.value",
      },
    ],
  },
  {
    icon: Upload,
    titleKey: "features.oneFlow.title",
    descriptionKey: "features.oneFlow.description",
    stats: [
      {
        labelKey: "features.oneFlow.stats.upload.label",
        valueKey: "features.oneFlow.stats.upload.value",
      },
      {
        labelKey: "features.oneFlow.stats.review.label",
        valueKey: "features.oneFlow.stats.review.value",
      },
      {
        labelKey: "features.oneFlow.stats.control.label",
        valueKey: "features.oneFlow.stats.control.value",
      },
    ],
  },
  {
    icon: BarChart3,
    titleKey: "features.deepIntelligence.title",
    descriptionKey: "features.deepIntelligence.description",
    stats: [
      {
        labelKey: "features.deepIntelligence.stats.before.label",
        valueKey: "features.deepIntelligence.stats.before.value",
      },
      {
        labelKey: "features.deepIntelligence.stats.after.label",
        valueKey: "features.deepIntelligence.stats.after.value",
      },
      {
        labelKey: "features.deepIntelligence.stats.why.label",
        valueKey: "features.deepIntelligence.stats.why.value",
      },
    ],
  },
];

export function Features() {
  const { t } = useJsonTranslation("HomeFeatures");

  return (
    <div
      data-home-block="feature-highlights"
      className="relative overflow-hidden px-5 py-6 sm:px-7 sm:py-8"
    >
      {/* Ambient light */}
      <div className="pointer-events-none absolute inset-0">
        <div className="bg-leaf-500/8 absolute top-1/4 right-1/4 h-96 w-96 rounded-full blur-[140px]" />
        <div className="bg-leaf-500/6 absolute bottom-1/4 left-1/4 h-96 w-96 rounded-full blur-[140px]" />
      </div>

      <div className="relative z-10">
        {/* Section Header */}
        <div className="mb-10 text-center">
          <div className="mb-6 flex flex-wrap items-center justify-center gap-3">
            <div className="glass inline-flex items-center gap-2 rounded-full px-4 py-2">
              <span className="text-muted-foreground text-sm">
                {t("header.badge")}
              </span>
            </div>
            <div className="bg-leaf-500/10 border-leaf-500/20 inline-flex items-center gap-2 rounded-full border px-4 py-2">
              <span className="text-leaf-600 dark:text-leaf-400 text-sm font-medium">
                {t("header.patent")}
              </span>
            </div>
          </div>

          <h2 className="font-home-display text-foreground mb-6 text-3xl font-semibold tracking-normal text-balance md:text-4xl lg:text-5xl">
            {t("header.title")}
          </h2>

          <p className="text-muted-foreground mx-auto max-w-3xl text-lg text-pretty md:text-xl">
            {t("header.subtitle")}
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {features.map((feature, i) => (
            <div key={i} className="uni-card group relative overflow-hidden">
              {/* Hover glow */}
              <div
                className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-70"
                style={{
                  background:
                    "radial-gradient(circle, rgba(52,211,153,0.35), transparent 70%)",
                }}
                aria-hidden="true"
              />

              <div className="relative flex items-start gap-4">
                <div className="bg-surface-success border-surface-success-border text-leaf-700 dark:text-leaf-300 flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border shadow-[0_12px_28px_-14px_rgba(52,211,153,0.6)] transition-transform duration-300 group-hover:scale-110">
                  <feature.icon className="h-6 w-6" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-display text-foreground mb-2 text-lg font-semibold">
                    {t(feature.titleKey)}
                  </h3>
                  <p className="text-muted-foreground mb-4 text-sm leading-relaxed">
                    {t(feature.descriptionKey)}
                  </p>
                  <div className="flex flex-wrap gap-3 sm:gap-4">
                    {feature.stats.map((stat, j) => (
                      <div key={j} className="text-center">
                        <div className="text-foreground text-sm font-bold tabular-nums">
                          {t(stat.valueKey)}
                        </div>
                        <div className="text-muted-foreground/60 text-xs">
                          {t(stat.labelKey)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Feature Pills */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-2.5">
          {[
            "pills.oneClickOptimization",
            "pills.fiveModels",
            "pills.successFromFirstSecond",
            "pills.basedOnYourContent",
            "pills.fullChannelAnalysis",
            "pills.prioritySupport",
          ].map((pill, i) => (
            <div
              key={i}
              className="glass glass-hover text-muted-foreground hover:text-foreground/90 rounded-full px-4 py-2 text-sm transition-all duration-300 hover:-translate-y-0.5"
            >
              {t(pill)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
